/* eslint-disable @typescript-eslint/camelcase */
import {
  Grid,
  makeStyles,
  Tooltip,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { Build, InfoOutlined, ReportProblem } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import Button from 'components/Button';
import ToggleCard from 'components/ToggleCard';
import { Formik } from 'formik';
import i18n from 'i18n';
import { editEnvironmentsRequest } from 'modules/environments/actions';
import { EnvironmentFields } from 'modules/environments/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import TitleBehavior from '../FormBehavior/TitleBehavior';
import styles from './styles';
import AdvisorHistory from './AdvisorHistory';

const useStyles = makeStyles(styles);

interface EnvironmentAdvisorsProps {
  environmentSelected: EnvironmentFields | null;
  isLoading: boolean;
  editEnvironmentsRequest: ({ id, values }) => void;
}
const EnvironmentAdvisors = ({
  environmentSelected,
  isLoading,
  editEnvironmentsRequest,
}: EnvironmentAdvisorsProps) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();

  const onSubmit = values => {
    editEnvironmentsRequest({ id, values });
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <TitleBehavior title={i18n.t('ADVISORS_CONFIGURATIONS_KEY')} />

        <Grid item spacing={2} container xs={12} alignItems="baseline">
          <Formik
            initialValues={{
              should_apply_advisor_automatically:
                environmentSelected?.should_apply_advisor_automatically ||
                false,
              high_risk_environment:
                environmentSelected?.high_risk_environment || false,
            }}
            onSubmit={onSubmit}
            enableReinitialize
          >
            {({ setFieldValue, values }) => (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <ToggleCard
                      id="high-risk-environment-toggle"
                      active={values.high_risk_environment}
                      getActive={value => {
                        setFieldValue('high_risk_environment', !value);

                        if (!value) {
                          setFieldValue(
                            'should_apply_advisor_automatically',
                            false
                          );
                        }
                      }}
                      title={i18n.t('HIGH_RISK_ENVIRONMENT_KEY')}
                      icon={<ReportProblem />}
                    >
                      <Alert severity="info" className={classes.alert}>
                        <Typography className={classes.textAlert}>
                          {i18n.t('HIGH_RISK_ENVIRONMENT_ALERT_KEY')}
                        </Typography>
                      </Alert>
                    </ToggleCard>
                  </Grid>

                  <Grid item xs={6}>
                    <ToggleCard
                      id="should-apply-toggle"
                      active={values.should_apply_advisor_automatically}
                      disable={values.high_risk_environment}
                      getActive={value => {
                        setFieldValue(
                          'should_apply_advisor_automatically',
                          !value
                        );
                      }}
                      title={i18n.t('SHOULD_APPLY_ADVISOR_AUTOMATICALLY_KEY')}
                      icon={<Build />}
                      customSubheader={
                        <Tooltip
                          title={i18n.t(
                            'SHOULD_APPLY_ADVISOR_AUTOMATICALLY_DESCRIPTION_KEY'
                          )}
                        >
                          <InfoOutlined className={classes.infoIcon} />
                        </Tooltip>
                      }
                    >
                      <Alert
                        severity="warning"
                        color="info"
                        className={classes.alert}
                      >
                        <Typography className={classes.textAlert}>
                          {i18n.t(
                            'SHOULD_APPLY_ADVISOR_AUTOMATICALLY_ALERT_KEY'
                          )}
                        </Typography>
                      </Alert>
                    </ToggleCard>
                  </Grid>
                </Grid>

                <Grid container justify="flex-end" className={classes.buttons}>
                  <Button
                    id="submit-button"
                    type="submit"
                    submitButton
                    onClick={() => onSubmit(values)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={15} />
                    ) : (
                      i18n.t('SAVE_KEY')
                    )}
                  </Button>
                </Grid>
              </>
            )}
          </Formik>
        </Grid>
      </Grid>

      <AdvisorHistory />
    </Grid>
  );
};

const mapStateToProps = ({
  environments: {
    environmentSelected,
    isLoading,
    isLoadingEnvironmentAdvisorsList,
  },
}: ApplicationState) => ({
  environmentSelected,
  isLoading: isLoading || isLoadingEnvironmentAdvisorsList,
});

export default connect(mapStateToProps, {
  editEnvironmentsRequest,
})(EnvironmentAdvisors);
